import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  catchError,
  EMPTY,
  finalize,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { SnackbarService } from './snackbar.service';
import { Router } from '@angular/router';
import { AuthRepository } from '../auth/state/auth.repository';
import { ProfileRepository } from '../../features/private/profile/state/profile.repository';
import { ISignInData } from '../../shared/models/auth/sign-in';
import { IRegisterData } from '../../shared/models/auth/register';
import { IConfirmEmailData } from '../../shared/models/auth/confirm-email';
import { ITokenData } from '../../shared/models/auth/token';
import { UiRepository } from '../state/ui.repository';
import { ProfileInitialRepository } from 'src/app/features/private/profile/state/profile-initial.repository';
import { ProfileMainRepository } from 'src/app/features/private/profile/state/profile-main.repository';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _http = inject(HttpClient);
  private _authRepo = inject(AuthRepository);
  private _profileRepo = inject(ProfileRepository);
  private _profileInitialRepo = inject(ProfileInitialRepository);
  private _profileMainRepo = inject(ProfileMainRepository);
  private _uiRepo = inject(UiRepository);
  private _snackbar = inject(SnackbarService);
  private _router = inject(Router);

  private baseUrl = environment.apiUrl;
  private _role = this._profileInitialRepo.role$;
  clientId = environment.settings.auth.clientId;
  clientSecret = environment.settings.auth.secretId;

  login(data: ISignInData): void {
    this._authRepo.setLoading(true);

    const userData = {
      emailOrUsername: data.email,
      password: data.password,
      rememberMe: true,
    };

    this._http
      .post<{ data: { token: ITokenData }; errors: any[] }>(
        `${this.baseUrl}/auth/login/start-email`,
        userData
      )
      .pipe(
        tap((resp: { data: { token: ITokenData }; errors: any[] }) => {
          if (resp.data.token.accessToken !== null) {
            this._authRepo.updateToken(resp.data.token);
            // const shortLivedToken = {
            //   ...resp.data.token,
            //   expiresDate: new Date(Date.now() + 30 * 1000)
            // };
            // this._authRepo.updateToken(shortLivedToken);
            this._router.navigate(['private/events']);
          }
          this._uiRepo.updateIsOpenedSidenav(true);
          this._authRepo.setLoading(false);
        }),
        catchError((error) => {
          console.error('%c error in login', 'color: red', error);
          this._authRepo.setLoading(false);
          return EMPTY;
        })
      )
      .subscribe();
  }

  register(data: IRegisterData): Observable<void> {
    return of(0).pipe(
      tap(() => {
        this._authRepo.setLoading(true);
      }),
      switchMap(() =>
        this._http.post<void>(`${this.baseUrl}/auth/register/start`, data)
      ),
      tap(() => {
        this._snackbar.success('Код подтверждения отправлен на почту.');
        // this._router.navigate(['private/profile']);
        this._authRepo.setLoading(false);
      }),
      catchError((err) => {
        this._authRepo.setLoading(false);
        return EMPTY;
      }),
      map(() => undefined)
    );
  }

  confirmEmail(token: IConfirmEmailData): Observable<void> {
    return of(0).pipe(
      tap(() => {
        this._authRepo.setLoading(true);
      }),
      switchMap(() =>
        this._http.post<{ data: ITokenData }>(
          `${this.baseUrl}/auth/register/confirm-email`,
          { ...token }
        )
      ),
      tap((resp: { data: ITokenData }) => {
        if (resp.data.accessToken !== null) {
          this._authRepo.updateToken(resp.data);
          // this._router.navigate(['private/quiz']);
          this._router.navigate(['/private/profile']);
          this._snackbar.success('Email подтвержден успешно!');
        }
        this._authRepo.setLoading(false);
      }),
      catchError((err) => {
        this._authRepo.setLoading(false);
        console.error('%c error in confirmEmail', 'color: red', err);
        return EMPTY;
      }),
      map(() => undefined)
    );
  }

  logout(): Observable<void> {
    // Set a logging out flag
    this._authRepo.setLoggingOut(true);

    return this._http.post<void>(`${this.baseUrl}/auth/logout`, {}).pipe(
      tap(() => {
        // Clear all state
        this._authRepo.updateToken(null);
        this._profileRepo.setProfile(null);
        this._profileInitialRepo.setProfileInitial(null);
        this._profileMainRepo.setProfileMain(null);

        // Navigate after state is cleared
        this._router.navigate(['public', 'login']);
      }),
      finalize(() => {
        this._authRepo.setLoggingOut(false);
      }),
      catchError((err) => {
        console.error('%c error in logout', 'color: red', err);
        return EMPTY;
      })
    );
  }

  refreshToken(): Observable<ITokenData> {
    // Получаем токен обновления из localStorage
    const storedData = localStorage.getItem('auth');
    if (!storedData) {
      this._router.navigateByUrl('/public/login');
      return throwError(() => new Error('Refresh token не найден'));
    }

    const userData = JSON.parse(storedData || '{}');
    if (!userData.token?.refreshToken?.token) {
      this._router.navigateByUrl('/public/login');
      return throwError(() => new Error('Refresh token не найден'));
    }

    return this._http
      .post<{ data: ITokenData }>(`${this.baseUrl}/auth/refresh`, {
        refreshToken: userData.token.refreshToken.token,
      })
      .pipe(
        map((response) => response.data),
        tap((tokenData) => {
          this._authRepo.updateToken(tokenData);
        })
      );
  }

  getToken(): string | null {
    const storedData = localStorage.getItem('auth');
    if (storedData) {
      const userData = JSON.parse(storedData);
      if (userData.token?.accessToken) {
        return userData.token.accessToken;
      }
    }
    return null;
  }
}
